<ion-header [translucent]="true">
  <ion-toolbar>
    <ion-title>{{ "CHECKOUT.CHECKOUT" | translate }}</ion-title>
    <ion-buttons slot="start">
      <ion-back-button defaultHref="cart" text="{{ 'COMMON.BACK' | translate }}" />
    </ion-buttons>
  </ion-toolbar>
</ion-header>

<ion-content>
  <div class="flex justify-center">
    <div class="h-full w-full max-w-[750px] pb-6">
      @if (cartStore.items().length) {
        @if (!cartStore.onlyDigitalProducts()) {
          <ion-card class="border-none">
            <ion-item lines="none">
              <ion-text class="font-bold">{{ "CHECKOUT.DELIVERY_ADDRESS" | translate }}</ion-text>
              <ion-button
                slot="end"
                fill="clear"
                color="primary"
                [routerLink]="['/account/addresses']"
                [queryParams]="{ pickerMode: true, redirect: '/cart/checkout' }"
              >
                <ion-text class="text-[1rem]">
                  {{ (cartStore.address() ? "COMMON.CHANGE" : "COMMON.ADD") | translate }}
                </ion-text>
              </ion-button>
            </ion-item>
            @if (cartStore.address(); as address) {
              <div class="bg-light ms-4 h-[1px]"></div>
              <ion-item lines="none">
                <ion-text>
                  {{ address.label }}
                  <span class="text-medium ms-1">{{ address.street }}</span>
                </ion-text>
              </ion-item>
            }
          </ion-card>
        }
        <ion-card class="border-none py-2">
          <ion-item lines="none">
            <ion-text class="font-bold">{{ "ORDERS.PAYMENT_METHOD" | translate }}</ion-text>
          </ion-item>
          <div class="bg-light mb-2 h-[1px]"></div>
          <ion-item lines="none">
            <div class="w-full">
              <div class="mt-2">
                <ion-radio-group
                  (ionChange)="cartStore.setPaymentMethod($event.detail.value)"
                  [value]="cartStore.paymentMethod()"
                >
                  @for (method of paymentMethods; track $index) {
                    <ion-item [lines]="$index === paymentMethods.length - 1 ? 'none' : 'full'" class="rounded-md">
                      <ion-icon slot="start" [name]="method"></ion-icon>
                      <ion-radio [value]="method" alignment="end">
                        {{ "ENUMS.PaymentMethod." + method | translate | titlecase }}
                      </ion-radio>
                    </ion-item>
                  }
                </ion-radio-group>
              </div>
            </div>
          </ion-item>
        </ion-card>
      } @else {
        <div class="flex h-[calc(95%)] flex-col items-center justify-center">
          <mtjr-dynamic-img
            src="/assets/no_orders.svg"
            darkSrc="/assets/no_orders_dark.svg"
            class="size-[220px] opacity-80 dark:opacity-70"
          />
          <p class="text-medium/70 mt-2 text-center">{{ "CART.CART_IS_EMPTY" | translate }}</p>
        </div>
      }
      @if (cartStore.items().length) {
        <div class="bg-primary/5 mx-3 rounded-xl px-4 py-4">
          <ion-text class="font-bold">{{ "CHECKOUT.ORDER_SUMMARY" | translate }}</ion-text>
          <div class="mt-3 flex items-center">
            <ion-text class="text-medium text-sm">{{ "CHECKOUT.SUB_TOTAL" | translate }}</ion-text>
            <div class="flex-grow"></div>
            <ion-text class="text-md">{{ cartStore.subTotal() | vxCurrency }}</ion-text>
          </div>
          @if (cartStore.totalDiscount() > 0) {
            <div class="mt-2 flex items-center">
              <ion-text class="text-medium text-sm">{{ "CHECKOUT.DISCOUNT" | translate }}</ion-text>
              <div class="flex-grow"></div>
              <ion-text class="text-md">{{ cartStore.totalDiscount() | vxCurrency }}</ion-text>
            </div>
          }
          @if (!cartStore.onlyDigitalProducts()) {
            <div class="mt-2 flex items-center">
              <ion-text class="text-medium text-sm">{{ "CHECKOUT.SHIPPING" | translate }}</ion-text>
              <div class="flex-grow"></div>
              @if (cartStore.calculateShippingCostLoading()) {
                <ion-text class="text-md text-medium">...</ion-text>
              } @else {
                <ion-text class="text-md">{{ cartStore.shippingCost() | vxCurrency }}</ion-text>
              }
            </div>
          }
          <div class="bg-medium/5 mb-2 mt-3 h-[2px] w-full"></div>
          <div class="mt-2 flex items-center">
            <ion-text class="text-medium text-sm">{{ "CHECKOUT.TOTAL" | translate }}</ion-text>
            <div class="flex-grow"></div>
            <ion-text class="text-md text-primary font-bold">
              {{ cartStore.subTotal() - cartStore.totalDiscount() + cartStore.shippingCost() | vxCurrency }}
            </ion-text>
          </div>
        </div>
        <div class="mx-4 my-2">
          <mtjr-error-bar [failure]="cartStore.placeOrderFailure() || cartStore.checkoutFailure()" />
        </div>
        <mtjr-submit-button
          expand="block"
          [state]="cartStore.placeOrderState()"
          color="primary"
          class="mx-3 mt-3 h-10 capitalize"
          [label]="'CHECKOUT.CONFIRM' | translate"
          (click)="checkout()"
        />
      }
      <ion-card class="mt-5 border-none">
        <ion-list class="bg-transparent !p-0">
          @for (item of cartStore.items(); track $index) {
            <div
              class="flex items-center px-4 py-3 hover:bg-light cursor-pointer"
              [routerLink]="['/vendors', item.product.vendorId, 'products', item.product.id]"
              routerDirection="forward"
            >
              <img
                [src]="item.product.images?.[0] || item.variation?.image"
                class="w-20 aspect-square rounded-xl p-[0.2px] border border-solid border-medium/30 me-2 object-cover"
                alt="thumbnail"
              />
              <div class="ms-2 mt-1 flex flex-1 flex-col gap-1">
                <div class="flex flex-wrap items-center justify-end gap-2 pe-2">
                  <ion-text class="me-auto pe-4 font-bold">{{ item.product.name }}</ion-text>
                  @for (attr of item.variation?.attributes; track $index) {
                    @if (attr.type !== "color") {
                      <div class="border-primary text-primary bg-primary/10 min-w-6 rounded-full px-2 py-1 text-center">
                        <ion-label>
                          {{ "ATTRIBUTE." + attr.typeLabel.toUpperCase() | translate }} - {{ attr.label }}
                        </ion-label>
                      </div>
                    } @else {
                      <div
                        fill="clear"
                        class="border-medium !m-0 flex size-6 items-center justify-center rounded-full border-2 border-solid !p-0 text-white"
                        [ngStyle]="{
                          'background-color': attr.key,
                        }"
                      ></div>
                    }
                  }
                </div>
                <div class="flex flex-wrap">
                  <ion-text color="primary" class="text-md me-2 break-keep">
                    {{
                      (item.variation?.price || item.product.price || 0) -
                        (item.product.discount || item.variation?.discount || 0) | vxCurrency
                    }}
                  </ion-text>
                  @if (item.product.discount ?? item.variation?.discount ?? 0) {
                    <ion-text color="medium" class="break-keep text-sm line-through">
                      {{ item.product.discount ?? item.variation?.discount ?? 0 | vxCurrency }}
                    </ion-text>
                  }
                </div>
                <ion-text class="font-bold">
                  {{ item.quantity.toString().padStart(2, "0") }} {{ "CHECKOUT.ITEMS" | translate }}
                </ion-text>
              </div>
            </div>
            @if ($index !== cartStore.items().length - 1) {
              <div class="bg-light ms-32 h-[1px] w-full"></div>
            }
          }
        </ion-list>
      </ion-card>
    </div>
  </div>
</ion-content>
